import React from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// World map topology data (110m resolution from natural earth)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Countries where Selimarine has operated
const countries = ['Senegal', 'Gabon', 'Turkey', 'Lebanon'];

// Coordinates [longitude, latitude] for office/project markers
const markers = [
  { name: 'Istanbul, Turkey', coordinates: [28.9784, 41.0082], type: 'office' },
  { name: 'Dakar, Senegal', coordinates: [-17.4677, 14.7167], type: 'office' },
  { name: 'Gabon', coordinates: [11.6094, -0.8037], type: 'project' },
  { name: 'Lebanon', coordinates: [35.8623, 33.8547], type: 'project' }
];

const GlobalReachMap = () => {
  const { t } = useTranslation();
  return (
    <div className="w-full">
      <div className="relative bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
        <ComposableMap
          projectionConfig={{
            scale: 180,
            center: [10, 20]
          }}
          width={980}
          height={500}
          style={{ width: '100%', height: 'auto' }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isHighlighted = countries.includes(geo.properties.name);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isHighlighted ? '#f59e0b' : '#1e3a5f'}
                    stroke={isHighlighted ? '#fbbf24' : '#0f172a'}
                    strokeWidth={isHighlighted ? 1 : 0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { 
                        fill: isHighlighted ? '#fbbf24' : '#2d4a73',
                        outline: 'none',
                        cursor: 'pointer'
                      },
                      pressed: { outline: 'none' }
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Office/Project Markers */}
          {markers.map((marker) => (
            <Marker key={marker.name} coordinates={marker.coordinates}>
              <g>
                {/* Pulsing ring animation */}
                <circle
                  r={8}
                  fill={marker.type === 'office' ? '#ef4444' : '#10b981'}
                  fillOpacity={0.3}
                >
                  <animate
                    attributeName="r"
                    values="8;16;8"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="fill-opacity"
                    values="0.3;0;0.3"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
                {/* Main marker dot */}
                <circle
                  r={4}
                  fill={marker.type === 'office' ? '#ef4444' : '#10b981'}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </g>
              <text
                textAnchor="middle"
                y={-12}
                style={{
                  fontFamily: 'system-ui, sans-serif',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  fill: '#fff',
                  stroke: '#0f172a',
                  strokeWidth: 3,
                  paintOrder: 'stroke',
                  pointerEvents: 'none'
                }}
              >
                {marker.name.split(',')[0]}
              </text>
            </Marker>
          ))}
        </ComposableMap>

        {/* Legend - overlay on desktop, hidden on mobile (shown below map instead) */}
        <div className="hidden md:block absolute bottom-4 left-4 bg-slate-800/90 backdrop-blur-sm rounded-lg p-4 border border-slate-700">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-white"></span>
              <span className="text-xs text-white font-medium">{t('home.featured.globalReach.officeLocations')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-green-500 ring-2 ring-white"></span>
              <span className="text-xs text-white font-medium">{t('home.featured.globalReach.projectOperations')}</span>
            </div>
            <div className="flex items-center space-x-2 pt-1 border-t border-slate-700">
              <span className="w-3 h-3 rounded-sm bg-amber-500"></span>
              <span className="text-xs text-white font-medium">{t('home.featured.globalReach.countriesServed')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only legend (shown below the map) */}
      <div className="md:hidden mt-4 bg-slate-800 rounded-lg p-4 border border-slate-700">
        <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-white"></span>
            <span className="text-xs text-white font-medium">{t('home.featured.globalReach.officeLocations')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-green-500 ring-2 ring-white"></span>
            <span className="text-xs text-white font-medium">{t('home.featured.globalReach.projectOperations')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-sm bg-amber-500"></span>
            <span className="text-xs text-white font-medium">{t('home.featured.globalReach.countriesServed')}</span>
          </div>
        </div>
      </div>

      {/* Stats Bar Below Map */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white p-4 rounded-lg shadow-md text-center"
        >
          <div className="text-3xl mb-1">🇹🇷</div>
          <div className="font-bold text-slate-900 text-sm">Turkey</div>
          <div className="text-xs text-gray-600">Istanbul HQ</div>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          viewport={{ once: true }}
          className="bg-white p-4 rounded-lg shadow-md text-center"
        >
          <div className="text-3xl mb-1">🇸🇳</div>
          <div className="font-bold text-slate-900 text-sm">Senegal</div>
          <div className="text-xs text-gray-600">Dakar Office</div>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white p-4 rounded-lg shadow-md text-center"
        >
          <div className="text-3xl mb-1">🇬🇦</div>
          <div className="font-bold text-slate-900 text-sm">Gabon</div>
          <div className="text-xs text-gray-600">Project Site</div>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          viewport={{ once: true }}
          className="bg-white p-4 rounded-lg shadow-md text-center"
        >
          <div className="text-3xl mb-1">🇱🇧</div>
          <div className="font-bold text-slate-900 text-sm">Lebanon</div>
          <div className="text-xs text-gray-600">Project Site</div>
        </motion.div>
      </div>
    </div>
  );
};

export default GlobalReachMap;
